import * as uFuzzyPkg from "@leeoniya/ufuzzy";
import {useLingui} from "@lingui/react";
import {Trans} from "@lingui/react/macro";
import {useMemo, useRef, useState} from "react";
import {useNavigate} from "react-router";
import createId, {IdType} from "../../models/create-id";
import type {
  GroupRepo,
  ItemEntry,
  ItemId,
  ItemVariantEntry,
  ItemVariantId,
} from "../../models/GroupRepo";
import {useIncompleteVariants} from "../../models/item-queries";
import PlusMinusButtons from "../../shared/PlusMinusButtons";
import {blueTheme, greenTheme, tealTheme} from "../../styles/themes/color-themes.css";
import {Box, Col, css, Row} from "../../ui/Box";
import Button from "../../ui/Button";
import Icon from "../../ui/Icon";
import {Input} from "../../ui/Input";
import {ItemPillLayout, MiniPill, QuantityPill} from "./ItemPill";
import RefineItem from "./RefineItem";

// the d.ts uses `export =`, whose constructable type only survives via `typeof import()`
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
const uFuzzy = (uFuzzyPkg as unknown as {default: typeof import("@leeoniya/ufuzzy")}).default;

// eslint-disable-next-line react-refresh/only-export-components
export const useSuggestions = <T,>(
  rawSearchVal: string,
  list: T[],
  getSearchTerm: (el: T) => string
) => {
  const [uf] = useState(() => new uFuzzy());
  const searchVal = uFuzzy.latinize(rawSearchVal);
  const refs = useRef({getSearchTerm});
  refs.current = {getSearchTerm};
  const haystack = useMemo(
    () => list.map((el) => uFuzzy.latinize(refs.current.getSearchTerm(el))),
    // callers pass an inline getSearchTerm, so it's read through a ref to only rebuild on list changes
    // eslint-disable-next-line react/memo-dependencies
    [list]
  );

  const idxs = uf.filter(haystack, uFuzzy.latinize(searchVal));
  if (!idxs || idxs.length === 0) return [];
  // idxs can be null when the needle is non-searchable (has no alpha-numeric chars)
  // sort/rank only when <= 1,000 items
  const infoThreshold = 1e3;

  if (idxs.length <= infoThreshold) {
    const info = uf.info(idxs, haystack, searchVal);
    // order is a double-indirection array (a re-order of the passed-in idxs)
    // this allows corresponding info to be grabbed directly by idx, if needed
    const order = uf.sort(info, haystack, searchVal);
    // using info.idx here instead of idxs because uf.info() may have
    // further reduced the initial idxs based on prefix/suffix rules
    return order.map((i) => list[info.idx[i]]);
  } else {
    // render pre-filtered but unordered matches
    return idxs.map((i) => list[i]);
  }
};

const ShowVariant = ({
  variant,
  groupRepo,
  item,
}: {
  variant: ItemVariantEntry;
  groupRepo: GroupRepo;
  item: ItemEntry;
}) => {
  const handleUpdate = (next: number) => {
    groupRepo.ItemVariants.patch(variant.id, {quantity: next, completedAt: null});
  };
  const handleDelete = () => {
    groupRepo.ItemVariants.patch(variant.id, {completedAt: new Date()});
  };
  return (
    <ItemPillLayout emoji={item.emoji} theme={tealTheme} name={item.name}>
      <Row flex="none" relative>
        <Row wrap sp="4" absolute justify="end" bottom="0" right="100%" px="4" pb="4">
          <QuantityPill variant={variant} />
          {variant.variant && <MiniPill>{variant.variant}</MiniPill>}
        </Row>
        <PlusMinusButtons
          value={variant.quantity}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
        />
      </Row>
    </ItemPillLayout>
  );
};

const AddVariant = ({
  item,
  groupRepo,
  onSelected,
  hasVariantsAlready,
}: {
  item: ItemEntry;
  groupRepo: GroupRepo;
  onSelected: (id: ItemVariantId) => void;
  hasVariantsAlready: boolean;
}) => {
  const handleClick = () => {
    const variantId = createId(IdType.ItemVariant);
    groupRepo.ItemVariants.set(variantId, {
      id: variantId,
      itemId: item.id,
      variant: null,
      quantity: 1,
      unit: null,
      completedAt: null,
    });
    onSelected(variantId);
  };
  return (
    <ItemPillLayout
      emoji={item.emoji}
      theme={blueTheme}
      name={item.name}
      onClick={handleClick}
      confirmAnimation="added"
    >
      {hasVariantsAlready ? (
        <Row align="center" pr="4">
          <MiniPill>
            <Trans>Add new variant</Trans>
          </MiniPill>
        </Row>
      ) : null}
    </ItemPillLayout>
  );
};

const ShowItem = ({
  item,
  onSelected,
  groupRepo,
  variants,
}: {
  item: ItemEntry;
  onSelected: (id: ItemVariantId) => void;
  groupRepo: GroupRepo;
  variants: ItemVariantEntry[] | null;
}) => {
  return (
    <>
      {variants?.map((variant) => (
        <ShowVariant variant={variant} item={item} key={variant.id} groupRepo={groupRepo} />
      ))}
      <AddVariant
        item={item}
        groupRepo={groupRepo}
        onSelected={onSelected}
        hasVariantsAlready={variants !== null}
      />
    </>
  );
};

type CreateItemPillProps = {
  name: string;
  groupRepo: GroupRepo;
  onDone: (id: ItemVariantId) => void;
};
export const CreateItemPill = (props: CreateItemPillProps) => {
  const {name, groupRepo, onDone} = props;
  const handleAddItem = () => {
    const itemId = createId(IdType.Item);
    const item: ItemEntry = {
      id: itemId,
      name,
      createdAt: new Date(),
      categoryId: null,
      emoji: null,
    };
    groupRepo.Items.set(itemId, item);
    const variantId = createId(IdType.ItemVariant);
    groupRepo.ItemVariants.set(variantId, {
      id: variantId,
      itemId: itemId,
      variant: null,
      quantity: 1,
      unit: null,
      completedAt: null,
    });
    return onDone(variantId);
  };
  return (
    <ItemPillLayout
      onClick={handleAddItem}
      emoji={<Icon name="plus" />}
      theme={greenTheme}
      name={name}
      confirmAnimation="added"
    />
  );
};

type SuggestionsProps = {
  searchVal: string;
  onSelected: (variantId: ItemVariantId, opts: {isNew: boolean}) => void;
  groupRepo: GroupRepo;
  variantsByItemId: ItemVariantsByItemId;
};
const Suggestions = ({searchVal, onSelected, groupRepo, variantsByItemId}: SuggestionsProps) => {
  const items = Object.values(groupRepo.Items.useGetAll());
  const rawSuggs = useSuggestions(searchVal.trimStart(), items, (i) => i.name);
  const shownSuggs = !searchVal ? items : rawSuggs;
  const suggs = shownSuggs.length > 15 ? shownSuggs.slice(0, 15) : shownSuggs;

  const shouldShowAddEl = () => {
    if (!searchVal.trim()) return false;
    if (suggs.length === 0) return true;
    const firstRes = suggs[0];
    return searchVal.trim().toLowerCase() !== firstRes.name.toLowerCase();
  };

  return (
    <Box overflow="auto" flex="auto">
      <Col sp="8">
        {suggs.map((item) => (
          <ShowItem
            key={item.id}
            item={item}
            onSelected={(id) => onSelected(id, {isNew: false})}
            groupRepo={groupRepo}
            variants={variantsByItemId.get(item.id) ?? null}
          />
        ))}
        {shouldShowAddEl() && (
          <CreateItemPill
            name={searchVal}
            onDone={(id) => onSelected(id, {isNew: true})}
            groupRepo={groupRepo}
          />
        )}
      </Col>
    </Box>
  );
};

type ItemVariantsByItemId = Map<ItemId, ItemVariantEntry[]>;

const useIncompleteVariantsByItemId = (groupRepo: GroupRepo) => {
  const incompleteVariants = useIncompleteVariants(groupRepo);
  return useMemo(() => {
    const map: ItemVariantsByItemId = new Map();
    for (const variant of incompleteVariants) {
      const existing = map.get(variant.itemId);
      if (existing) {
        existing.push(variant);
      } else {
        map.set(variant.itemId, [variant]);
      }
    }
    return map;
  }, [incompleteVariants]);
};

const AddItem = ({groupRepo}: {groupRepo: GroupRepo}) => {
  const [searchVal, setSearchVal] = useState("");
  const [refineVariantId, setRefineVariantId] = useState<ItemVariantId | null>(null);
  const [justAdded, setJustAdded] = useState<null | "new" | "existing">(null);
  const searchFieldRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();
  const variantsByItemId = useIncompleteVariantsByItemId(groupRepo);

  const handleInput = (e: string) => {
    if (refineVariantId) setRefineVariantId(null);
    setSearchVal(e);
  };

  const handleSelected = (variantId: ItemVariantId, {isNew}: {isNew: boolean}) => {
    setSearchVal("");
    searchFieldRef.current?.focus();
    setJustAdded(isNew ? "new" : "existing");
    setRefineVariantId(variantId);
  };

  const {i18n} = useLingui();
  const onClose = () => navigate("..");

  return (
    <Col
      sp="24"
      height="_addItemDrawerHeight"
      maxWidth="formWidth"
      width="100%"
      mr="auto"
      ml="auto"
    >
      <Row sp="8">
        <Input
          type="text"
          value={searchVal}
          onChange={handleInput}
          placeholder={
            justAdded
              ? i18n.t({id: "input.addNextItemPlaceholder"})
              : i18n.t({id: "input.addItemPlaceholder"})
          }
          ref={searchFieldRef}
          className={css({flex: "auto"})}
          data-drawer-autofocus
        />
        <Button onClick={onClose} variant="tertiary">
          <Icon name="close" />
        </Button>
      </Row>
      {refineVariantId ? (
        <RefineItem
          id={refineVariantId}
          justAdded={justAdded}
          onClose={onClose}
          groupRepo={groupRepo}
        />
      ) : (
        <Suggestions
          searchVal={searchVal}
          onSelected={handleSelected}
          groupRepo={groupRepo}
          variantsByItemId={variantsByItemId}
        />
      )}
    </Col>
  );
};

export default AddItem;
